/**
 * Safe Storage utilities for Safari Private Mode compatibility
 *
 * Provides localStorage/sessionStorage wrappers that gracefully
 * fall back to in-memory storage when browser storage is unavailable
 * (Safari Private Mode, storage quota exceeded, etc.)
 *
 * @example
 * ```typescript
 * import { safeLocalStorage, safeSessionStorage } from '@/services/utils/safeStorage';
 *
 * // Use exactly like native storage
 * safeLocalStorage.setItem('key', 'value');
 * const value = safeLocalStorage.getItem('key');
 *
 * // Check if using fallback
 * if (safeLocalStorage.isUsingFallback) {
 *   console.warn('Using in-memory storage');
 * }
 * ```
 */

export interface SafeStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  readonly isUsingFallback: boolean;
}

/**
 * SafeStorage implementation that wraps browser storage with fallback
 */
class SafeStorage implements SafeStorageAdapter {
  private storage: Storage | null = null;
  private fallback = new Map<string, string>();
  private _isUsingFallback = false;

  constructor(type: 'local' | 'session') {
    if (typeof window === 'undefined') {
      this._isUsingFallback = true;
      return;
    }

    try {
      const storage = type === 'local' ? window.localStorage : window.sessionStorage;
      // Test write to detect Safari Private Mode
      const testKey = '__storage_test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      this.storage = storage;
    } catch {
      this._isUsingFallback = true;
      console.warn(`[SafeStorage] ${type}Storage unavailable, using in-memory fallback`);
    }
  }

  get isUsingFallback(): boolean {
    return this._isUsingFallback;
  }

  getItem(key: string): string | null {
    if (this.storage) {
      try {
        return this.storage.getItem(key);
      } catch {
        return this.fallback.get(key) ?? null;
      }
    }
    return this.fallback.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (this.storage) {
      try {
        this.storage.setItem(key, value);
        return;
      } catch {
        // Quota exceeded or other error - fall through to fallback
        if (!this._isUsingFallback) {
          console.warn('[SafeStorage] Storage write failed, using fallback');
        }
      }
    }
    this.fallback.set(key, value);
  }

  removeItem(key: string): void {
    if (this.storage) {
      try {
        this.storage.removeItem(key);
      } catch {
        // Ignore
      }
    }
    this.fallback.delete(key);
  }

  clear(): void {
    if (this.storage) {
      try {
        this.storage.clear();
      } catch {
        // Ignore
      }
    }
    this.fallback.clear();
  }
}

// Singleton instances
export const safeLocalStorage = new SafeStorage('local');
export const safeSessionStorage = new SafeStorage('session');
